import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { todayWITA, formatDateLong, waLink, serviceCategory } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import RescheduleDialog from "@/components/RescheduleDialog";
import DetailDialog from "@/components/DetailDialog";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import {
  CalendarDots, CaretLeft, CaretRight, Prohibit, LockKeyOpen,
  WhatsappLogo, PlusCircle, ArrowsClockwise, Info, Stack,
} from "@phosphor-icons/react";

const CANCELLED = ["Batal", "No Show", "Dibatalkan Otomatis", "Dibatalkan", "Kedaluwarsa"];
const INACTIVE = [...CANCELLED, "Selesai"];
const isCancelled = (r) => CANCELLED.includes(r.status);

const CELL_STYLES = {
  available: "bg-white border-slate-200 text-slate-400 hover:border-[#7C4DE0] hover:text-[#7C4DE0] hover:bg-[#F7F0FF]",
  booked: "bg-[#7C4DE0] border-[#7C4DE0] text-white shadow-sm",
  buffer: "bg-amber-100 border-amber-300 text-amber-700",
  selesai: "bg-emerald-500 border-emerald-500 text-white",
  blocked: "bg-slate-700 border-slate-700 text-white",
  batal: "bg-slate-50 border-slate-200 text-slate-400 line-through",
};
const DOT_STYLES = {
  available: "bg-slate-300", booked: "bg-[#7C4DE0]", buffer: "bg-amber-400", selesai: "bg-emerald-500",
  blocked: "bg-slate-700", batal: "bg-slate-300",
};
const BOOKED_STYLES = {
  yoga: "bg-[#7C4DE0] border-[#7C4DE0] text-white shadow-sm",
  kb: "bg-teal-500 border-teal-500 text-white shadow-sm",
  bidan: "bg-rose-500 border-rose-500 text-white shadow-sm",
};
const SERVICE_LEGEND = [
  ["bg-rose-500", "Bidan Neny"],
  ["bg-teal-500", "KB"],
  ["bg-[#7C4DE0]", "Prenatal Yoga"],
];
const LEGEND = [
  ["available", "Tersedia"], ["booked", "Terisi Reservasi"], ["buffer", "Travel Buffer"],
  ["blocked", "Diblokir"], ["selesai", "Selesai"], ["batal", "Dibatalkan / No Show"],
];
const DOW = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];

// ---- date helpers (no timezone shift) ----
const fmt = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
const parse = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const addDays = (s, n) => { const dt = parse(s); dt.setDate(dt.getDate() + n); return fmt(dt); };
const mondayOf = (s) => { const dt = parse(s); const dow = (dt.getDay() + 6) % 7; dt.setDate(dt.getDate() - dow); return fmt(dt); };
const shortDate = (s) => { const [, m, d] = s.split("-"); return `${d}/${m}`; };

export default function CalendarView() {
  return (
    <ErrorBoundary message="Sebagian jadwal tidak dapat ditampilkan. Coba muat ulang halaman.">
      <CalendarViewInner />
    </ErrorBoundary>
  );
}

function CalendarViewInner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;
  const canManageAll = role === "admin" || role === "super_admin";

  const [view, setView] = useState("day"); // day | week
  const [date, setDate] = useState(todayWITA());
  const [data, setData] = useState({ slots: [], staff: [], reservations: [], blocked: [] });
  const [week, setWeek] = useState({ slots: [], staff: [], reservations: [], blocked: [] });
  const [loading, setLoading] = useState(true);
  const [petugasFilter, setPetugasFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [services, setServices] = useState([]);
  const [bookSvc, setBookSvc] = useState("");
  const [bookLok, setBookLok] = useState("Gerai");
  const [slot, setSlot] = useState(null);
  const [reschedule, setReschedule] = useState(null);
  const [detail, setDetail] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const weekStart = mondayOf(date);
  const weekEnd = addDays(weekStart, 6);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const loadDay = useCallback(() => {
    setLoading(true);
    const q = bookSvc ? `&service_id=${bookSvc}&lokasi=${encodeURIComponent(bookLok)}` : "";
    api.get(`/calendar?date=${date}${q}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [date, bookSvc, bookLok]);

  const loadWeek = useCallback(() => {
    setLoading(true);
    const q = bookSvc ? `&service_id=${bookSvc}&lokasi=${encodeURIComponent(bookLok)}` : "";
    api.get(`/calendar?start=${weekStart}&end=${weekEnd}${q}`)
      .then((r) => setWeek(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [weekStart, weekEnd, bookSvc, bookLok]);

  useEffect(() => { if (canManageAll) api.get("/services").then((r) => setServices(r.data || [])).catch(() => {}); }, [canManageAll]);

  useEffect(() => { if (view === "day") loadDay(); else loadWeek(); }, [view, loadDay, loadWeek]);

  const reload = () => { setSlot(null); if (view === "day") loadDay(); else loadWeek(); };

  const toMin = (t) => { const [h, m] = String(t).split(":").map(Number); return h * 60 + m; };
  const cellFor = (name, jam) => {
    const jm = toMin(jam);
    const rs = data.reservations.filter((r) => r.petugas_nama === name && (!serviceFilter || r.jenis_layanan === serviceFilter));
    const covers = (r) => { const st = toMin(r.jam_kunjungan || "00:00"); const dur = r._dur_min || 30; return st <= jm && jm < st + dur; };
    // ALL active reservations covering this slot (never dedupe per tanggal+jam).
    const list = rs.filter((x) => !INACTIVE.includes(x.status) && covers(x))
      .sort((a, b) => Number(toMin(b.jam_kunjungan) === jm) - Number(toMin(a.jam_kunjungan) === jm));
    const cancelled = rs.filter((x) => isCancelled(x) && toMin(x.jam_kunjungan) === jm);
    const withExtra = (c) => ({ ...c, list: c.list || (c.r ? [c.r] : []), cancelled });
    const ss = data.slot_status?.[name]?.[jam];
    if (ss) {
      const type = ss.status;
      if (type === "booked" || type === "buffer") {
        const r = list[0];
        if (!r) return withExtra({ type, r: null, isStart: false, orphan: true });
        return withExtra({ type, r, list, isStart: toMin(r.jam_kunjungan) === jm });
      }
      if (type === "selesai") {
        const r = rs.find((x) => x.status === "Selesai" && covers(x));
        if (!r) return withExtra({ type, r: null, isStart: false, orphan: true });
        return withExtra({ type, r, isStart: toMin(r.jam_kunjungan) === jm });
      }
      if (type === "blocked") {
        const b = data.blocked.find((x) => x.petugas_nama === name && x.jam === jam) || null;
        return withExtra({ type, b });
      }
      if (type === "batal") return withExtra({ type, r: null, list: [] });
      return withExtra({ type: "available", bookable: ss.bookable });
    }
    // Fallback (e.g. week view without slot_status): local derivation.
    if (list.length) return withExtra({ type: "booked", r: list[0], list, isStart: toMin(list[0].jam_kunjungan) === jm });
    const done = rs.find((r) => r.status === "Selesai" && covers(r));
    if (done) return withExtra({ type: "selesai", r: done, isStart: toMin(done.jam_kunjungan) === jm });
    const blk = data.blocked.find((b) => b.petugas_nama === name && b.jam === jam);
    if (blk) return withExtra({ type: "blocked", b: blk });
    if (cancelled.length) return withExtra({ type: "batal", r: null, list: [] });
    return withExtra({ type: "available" });
  };

  const staffList = (data.staff || []).filter((s) => !petugasFilter || s.nama === petugasFilter);
  const weekStaff = (week.staff || []).filter((s) => !petugasFilter || s.nama === petugasFilter);
  const serviceOptions = [...new Set(((view === "day" ? data.reservations : week.reservations) || []).map((r) => r.jenis_layanan).filter(Boolean))].sort();
  // Schedules RBAC: Super Admin & Admin manage all; Petugas manages ONLY their own schedule.
  // Ownership by canonical staff (from backend identity), NOT the stale name snapshot.
  const myStaffName = data?.identity?.staff_name || user?.petugas_nama;
  const canManage = (name) => canManageAll || (role === "petugas" && !!myStaffName && name === myStaffName);
  const openSlot = (name, jam) => setSlot({ staffName: name, jam, cell: cellFor(name, jam) });

  const weekCount = (name, day) => {
    const rs = week.reservations.filter((r) => r.petugas_nama === name && r.tanggal_kunjungan === day && !INACTIVE.includes(r.status) && (!serviceFilter || r.jenis_layanan === serviceFilter));
    const blocked = week.blocked.filter((b) => b.petugas_nama === name && b.tanggal === day).length;
    const cat = { yoga: 0, kb: 0, bidan: 0 };
    rs.forEach((r) => { cat[serviceCategory(r.jenis_layanan)] += 1; });
    return { booked: rs.length, blocked, cat };
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CalendarDots size={26} weight="duotone" className="text-[#7C4DE0]" />
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-800">Kalender Jadwal</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl border border-slate-200 bg-white p-1">
            <button data-testid="view-day" onClick={() => setView("day")} className={`rounded-lg px-3 py-1.5 text-sm font-bold ${view === "day" ? "bg-[#7C4DE0] text-white" : "text-slate-500"}`}>Harian</button>
            <button data-testid="view-week" onClick={() => setView("week")} className={`rounded-lg px-3 py-1.5 text-sm font-bold ${view === "week" ? "bg-[#7C4DE0] text-white" : "text-slate-500"}`}>Mingguan</button>
          </div>
          {(canManageAll || (role === "petugas" && myStaffName)) && (
          <button data-testid="bulk-block-open" onClick={() => setBulkOpen(true)} className="flex items-center gap-1.5 rounded-xl bg-slate-700 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800"><Stack size={16} weight="bold" /> Blokir Massal</button>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-rose-100/70 bg-white p-3 shadow-sm">
        <button data-testid="cal-prev" onClick={() => setDate(addDays(date, view === "day" ? -1 : -7))} className="rounded-xl border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"><CaretLeft size={18} weight="bold" /></button>
        <input data-testid="cal-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-[#7C4DE0]" />
        <button data-testid="cal-next" onClick={() => setDate(addDays(date, view === "day" ? 1 : 7))} className="rounded-xl border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"><CaretRight size={18} weight="bold" /></button>
        <button data-testid="cal-today" onClick={() => setDate(todayWITA())} className="rounded-xl bg-[#EEE6FF] px-3 py-2 text-sm font-bold text-[#6A38C7]">Hari Ini</button>
        {canManageAll && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <select data-testid="cal-service-filter" value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-[#7C4DE0]">
              <option value="">Semua Layanan</option>
              {serviceOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <select data-testid="cal-book-service" value={bookSvc} onChange={(e) => setBookSvc(e.target.value)} className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 outline-none focus:border-indigo-400">
              <option value="">Cek Booking: pilih layanan…</option>
              {services.map((s) => <option key={s.id || s._id} value={s.id || s._id}>{s.nama} ({s.durasi || "?"}m)</option>)}
            </select>
            {bookSvc && (
              <select data-testid="cal-book-lokasi" value={bookLok} onChange={(e) => setBookLok(e.target.value)} className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 outline-none focus:border-indigo-400">
                <option value="Gerai">Gerai</option>
                <option value="Homecare">Homecare</option>
              </select>
            )}
            <select data-testid="cal-petugas-filter" value={petugasFilter} onChange={(e) => setPetugasFilter(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-[#7C4DE0]">
              <option value="">Semua Petugas</option>
              {((view === "day" ? data.staff : week.staff) || []).map((s) => <option key={s.id} value={s.nama}>{s.nama}</option>)}
            </select>
          </div>
        )}
      </div>
      <p className="text-sm text-slate-500">{view === "day" ? formatDateLong(date) : `${shortDate(weekStart)} – ${shortDate(weekEnd)} ${weekEnd.slice(0, 4)}`}</p>

      {canManageAll && (
        <div data-testid="cal-pick-service-note" className={`rounded-xl border px-3 py-2 text-xs font-semibold ${bookSvc ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
          {bookSvc
            ? "Mode cek ketersediaan aktif — slot 'bisa' (hijau) valid untuk layanan + lokasi ini, sama persis dengan Booking Engine. Slot 'tak bisa' tidak dapat ditambah."
            : "Pilih layanan untuk cek ketersediaan: pakai selektor \"Cek Booking\" (layanan + lokasi) agar slot mendapat verdict Bisa/Tak Bisa. Tanpa itu, kalender hanya menampilkan keterisian dan penambahan reservasi dinonaktifkan."}
        </div>
      )}

      {/* Legend */}
      <div className="space-y-1.5">
        <div className="flex flex-wrap gap-3 rounded-xl bg-white/60 px-3 py-2 text-xs text-slate-500">
          {LEGEND.map(([k, label]) => (
            <span key={k} className="inline-flex items-center gap-1.5"><span className={`h-3 w-3 rounded-full ${DOT_STYLES[k]}`} />{label}</span>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 rounded-xl bg-white/60 px-3 py-2 text-xs text-slate-500">
          <span className="font-semibold text-slate-400">Terisi (per layanan):</span>
          {SERVICE_LEGEND.map(([c, label]) => (
            <span key={label} className="inline-flex items-center gap-1.5"><span className={`h-3 w-3 rounded-full ${c}`} />{label}</span>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-500" /></div>
      ) : view === "day" ? (
        staffList.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-rose-200 bg-white/50 p-12 text-center text-sm text-slate-500">Belum ada petugas aktif untuk ditampilkan.</div>
        ) : (
          <div className="space-y-4">
            {staffList.map((s) => (
              <div key={s.id} data-testid={`cal-staff-${s.nama}`} className="rounded-2xl border border-rose-100/70 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                  <span className="font-heading text-base font-bold text-slate-800">{s.nama}</span>
                  <span className="rounded-full bg-[#F7F0FF] px-2 py-0.5 text-xs font-semibold text-[#6A38C7]">{s.jenis}</span>
                  <span data-testid={`cal-interval-${s.nama}`} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Interval {s.slot_interval}m</span>
                  <span data-testid={`cal-daycount-${s.nama}`} className="ml-auto rounded-full bg-[#EEE6FF] px-2.5 py-0.5 text-[11px] font-bold text-[#6A38C7]">{data.reservations.filter((r) => r.petugas_nama === s.nama && !INACTIVE.includes(r.status) && (!serviceFilter || r.jenis_layanan === serviceFilter)).length} terisi</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(s.slots || data.slots).map((jam) => {
                    const cell = cellFor(s.nama, jam);
                    const isAvail = cell.type === "available";
                    const needPick = isAvail && !bookSvc;
                    const notBookable = isAvail && !!bookSvc && cell.bookable === false;
                    const canAddHere = isAvail && !!bookSvc && cell.bookable === true;
                    const cls = cell.type === "booked"
                      ? (BOOKED_STYLES[serviceCategory(cell.r?.jenis_layanan)] || CELL_STYLES.booked)
                      : notBookable ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-300 line-through"
                      : canAddHere ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      : needPick ? "border-slate-200 bg-white text-slate-400 cursor-help"
                      : CELL_STYLES[cell.type];
                    return (
                      <button key={jam} data-testid={`cal-slot-${s.nama}-${jam}`}
                        title={needPick ? "Klik untuk memblokir slot. Pilih layanan (Cek Booking) jika ingin menambah reservasi." : (notBookable ? "Tidak bisa dibooking untuk layanan/durasi ini — namun tetap bisa diblokir" : undefined)}
                        onClick={() => openSlot(s.nama, jam)}
                        className={`min-w-[64px] rounded-xl border px-2.5 py-2 text-center text-xs font-bold transition-colors ${cls}`}>
                        <span className="block leading-tight">{jam}</span>
                        {notBookable && <span className="mt-0.5 block text-[10px] font-medium">tak bisa</span>}
                        {canAddHere && <span className="mt-0.5 block text-[10px] font-medium">bisa</span>}
                        {cell.type === "booked" && <span className="mt-0.5 block truncate text-[10px] font-medium opacity-90">{cell.isStart ? (cell.r?.nama_lengkap || "Terisi") : "· lanjutan"}</span>}
                        {cell.type === "booked" && cell.list.length > 1 && <span data-testid={`cal-slot-count-${s.nama}-${jam}`} className="mt-0.5 block rounded-full bg-white/25 text-[10px] font-bold">+{cell.list.length - 1} lainnya</span>}
                        {cell.type === "buffer" && <span className="mt-0.5 block text-[10px] font-medium opacity-90">· buffer</span>}
                        {cell.type === "selesai" && <span className="mt-0.5 block text-[10px] font-medium opacity-90">{cell.isStart ? "Selesai" : "· lanjutan"}</span>}
                        {cell.type === "blocked" && <span className="mt-0.5 block text-[10px] font-medium opacity-90">Diblokir</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        weekStaff.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-rose-200 bg-white/50 p-12 text-center text-sm text-slate-500">Belum ada petugas aktif untuk ditampilkan.</div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-rose-100/70 bg-white shadow-sm">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-rose-100">
                  <th className="p-3 text-left text-xs font-bold text-slate-500">Petugas</th>
                  {weekDays.map((d) => (
                    <th key={d} className={`p-2 text-center text-xs font-bold ${d === todayWITA() ? "text-[#6A38C7]" : "text-slate-500"}`}>
                      {DOW[(parse(d).getDay() + 6) % 7]}<br /><span className="font-semibold">{shortDate(d)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weekStaff.map((s) => (
                  <tr key={s.id} data-testid={`cal-week-row-${s.nama}`} className="border-b border-rose-50 last:border-0">
                    <td className="p-3 font-semibold text-slate-700">{s.nama}<br /><span className="text-xs font-normal text-slate-400">{s.jenis}</span></td>
                    {weekDays.map((d) => {
                      const { booked, blocked, cat } = weekCount(s.nama, d);
                      const total = week.slots.length || 1;
                      const load = booked / total;
                      const bg = booked === 0 ? "bg-slate-50" : load < 0.34 ? "bg-emerald-100" : load < 0.67 ? "bg-amber-100" : "bg-rose-200";
                      return (
                        <td key={d} className="p-1.5 text-center">
                          <button data-testid={`cal-week-${s.nama}-${d}`} onClick={() => { setDate(d); setView("day"); }} className={`w-full rounded-lg ${bg} px-1 py-2 transition-transform hover:scale-105`}>
                            <span className="block text-sm font-bold text-slate-700">{booked}</span>
                            <span className="block text-[10px] text-slate-500">terisi</span>
                            {booked > 0 && (
                              <span className="mt-0.5 flex justify-center gap-0.5">
                                {cat.bidan > 0 && <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />}
                                {cat.kb > 0 && <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />}
                                {cat.yoga > 0 && <span className="h-1.5 w-1.5 rounded-full bg-[#7C4DE0]" />}
                              </span>
                            )}
                            {blocked > 0 && <span className="mt-0.5 block text-[10px] font-semibold text-slate-600">🔒 {blocked}</span>}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {slot && (
        <SlotSheet
          slot={slot} date={date} canManage={canManage(slot.staffName)}
          canAddReservation={!!bookSvc && slot.cell?.bookable === true}
          onClose={() => setSlot(null)} onReload={reload}
          onReschedule={(r) => { setSlot(null); setReschedule(r); }}
          onDetail={(r) => { setSlot(null); setDetail(r); }}
          onAdd={() => navigate(`/tambah?tanggal=${date}&jam=${slot.jam}&petugas=${encodeURIComponent(slot.staffName)}`)}
        />
      )}

      {bulkOpen && (
        <BulkBlockDialog
          onClose={() => setBulkOpen(false)} onDone={reload}
          staff={(view === "day" ? data.staff : week.staff) || []}
          slots={(view === "day" ? data.slots : week.slots) || []}
          defaultDate={date} canManageAll={canManageAll} myName={myStaffName}
        />
      )}

      <RescheduleDialog reservation={reschedule} open={!!reschedule} onClose={() => setReschedule(null)} onDone={() => { setReschedule(null); reload(); }} />
      <DetailDialog reservation={detail} open={!!detail} onClose={() => setDetail(null)} onChanged={reload} />
    </div>
  );
}

function SlotSheet({ slot, date, canManage, canAddReservation, onClose, onReload, onReschedule, onDetail, onAdd }) {
  const { staffName, jam, cell } = slot;
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const doBlock = async (confirmOverlap = false) => {
    setBusy(true);
    try { await api.post("/blocked-slots", { petugas_nama: staffName, tanggal: date, jam, alasan: reason, confirm_overlap: confirmOverlap }); toast.success("Slot berhasil diblokir"); onReload(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "block_overlap" && !confirmOverlap) {
        setBusy(false);
        if (window.confirm(`Peringatan: ${d.message}\n\nTetap blokir jam ini?`)) return doBlock(true);
        return;
      }
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  const doUnblock = async () => {
    if (!cell.b?.id) { toast.error("Data blokir tidak ditemukan. Muat ulang halaman."); return; }
    setBusy(true);
    try { await api.delete(`/blocked-slots/${cell.b.id}`); toast.success("Blokir slot dibuka"); onReload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="slot-sheet" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">{staffName} · {jam}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{formatDateLong(date)}</p>

        {(cell.list || []).length > 0 && (
          <div className="max-h-[50vh] space-y-3 overflow-y-auto" data-testid="slot-active-list">
            {cell.list.length > 1 && <p className="text-xs font-bold text-[#6A38C7]">{cell.list.length} reservasi aktif di slot ini</p>}
            {cell.list.map((r) => <SlotResCard key={r.id} r={r} buffer={cell.type === "buffer"} canManage={canManage} onDetail={onDetail} onReschedule={onReschedule} />)}
          </div>
        )}
        {(cell.cancelled || []).length > 0 && (
          <div data-testid="slot-cancelled-list" className="space-y-1.5 rounded-xl border border-slate-200 bg-slate-50 p-2.5">
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Dibatalkan</p>
            {cell.cancelled.map((r) => (
              <button key={r.id} data-testid={`slot-cancelled-${r.id}`} onClick={() => onDetail(r)} className="flex w-full items-center justify-between rounded-lg px-2 py-1 text-left text-xs text-slate-500 hover:bg-slate-100">
                <span className="truncate">{r.nama_lengkap} · {r.jenis_layanan}</span><span className="ml-2 shrink-0 font-semibold">{r.status}</span>
              </button>
            ))}
          </div>
        )}

        {cell.type === "blocked" && (
          <div className="space-y-3">
            <div className="rounded-xl bg-slate-100 p-3 text-sm text-slate-600">
              <p className="flex items-center gap-1.5 font-bold text-slate-700"><Prohibit size={16} weight="duotone" /> Slot Diblokir</p>
              {cell.b?.alasan ? <p className="mt-1 text-xs">Alasan: {cell.b.alasan}</p> : null}
            </div>
            {canManage ? (
              <button data-testid="slot-unblock-btn" onClick={doUnblock} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-2.5 text-sm font-bold text-white hover:bg-emerald-600 disabled:opacity-60"><LockKeyOpen size={18} weight="bold" /> {busy ? "Memproses..." : "Buka Blokir"}</button>
            ) : <p className="text-center text-xs text-slate-400">Anda tidak memiliki izin mengubah slot ini.</p>}
          </div>
        )}

        {cell.type === "available" && (
          <div className="space-y-3">
            {canManage ? (
              <>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-500">Alasan blokir (opsional)</label>
                  <input data-testid="slot-block-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="mis. Istirahat, rapat, cuti" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#7C4DE0]" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="slot-add-btn" disabled={!canAddReservation}
                    title={canAddReservation ? undefined : "Pilih layanan + lokasi (Cek Booking) untuk menambah reservasi di slot ini"}
                    onClick={() => canAddReservation ? onAdd() : toast.info("Pilih layanan + lokasi (Cek Booking) dulu untuk menambah reservasi di slot ini")}
                    className="flex items-center justify-center gap-1.5 rounded-xl bg-[#EEE6FF] py-2.5 text-sm font-bold text-[#6A38C7] hover:bg-[#E0D2FF] disabled:cursor-not-allowed disabled:opacity-50"><PlusCircle size={16} weight="bold" /> Tambah Reservasi</button>
                  <button data-testid="slot-block-btn" onClick={doBlock} disabled={busy} className="flex items-center justify-center gap-1.5 rounded-xl bg-slate-700 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-60"><Prohibit size={16} weight="bold" /> {busy ? "..." : "Blokir Slot"}</button>
                </div>
              </>
            ) : <p className="text-center text-xs text-slate-400">Slot tersedia. Anda tidak memiliki izin mengubah slot ini.</p>}
          </div>
        )}

        <DialogFooter>
          <button data-testid="slot-close-btn" onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Tutup</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkBlockDialog({ onClose, onDone, staff, slots, defaultDate, canManageAll, myName }) {
  const [action, setAction] = useState("block"); // block | clear
  const [petugas, setPetugas] = useState(canManageAll ? "__ALL__" : (myName || ""));
  const [start, setStart] = useState(defaultDate);
  const [end, setEnd] = useState(defaultDate);
  const [mode, setMode] = useState("full"); // full | custom
  const [jams, setJams] = useState([]);
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleJam = (j) => setJams((p) => (p.includes(j) ? p.filter((x) => x !== j) : [...p, j]));

  const submit = async () => {
    if (mode === "custom" && jams.length === 0) return toast.error("Pilih minimal satu jam");
    setBusy(true);
    const payload = {
      petugas_nama: canManageAll ? petugas : myName,
      start_date: start, end_date: end,
      full_day: mode === "full", jam_list: mode === "custom" ? jams : null,
      alasan,
    };
    try {
      if (action === "block") {
        const { data } = await api.post("/blocked-slots/bulk", payload);
        toast.success(`${data.created} slot diblokir · ${data.skipped_conflict} bentrok reservasi · ${data.skipped_existing} sudah diblokir`);
      } else {
        const { data } = await api.post("/blocked-slots/bulk-clear", payload);
        toast.success(`${data.deleted} blokir dibuka`);
      }
      onDone();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#7C4DE0]";

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-md" data-testid="bulk-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Blokir Massal (Cuti / Libur)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex rounded-xl border border-slate-200 p-1">
            <button data-testid="bulk-action-block" onClick={() => setAction("block")} className={`flex-1 rounded-lg py-1.5 text-sm font-bold ${action === "block" ? "bg-slate-700 text-white" : "text-slate-500"}`}>Blokir</button>
            <button data-testid="bulk-action-clear" onClick={() => setAction("clear")} className={`flex-1 rounded-lg py-1.5 text-sm font-bold ${action === "clear" ? "bg-emerald-500 text-white" : "text-slate-500"}`}>Buka Blokir</button>
          </div>

          {canManageAll && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Petugas</label>
              <select data-testid="bulk-petugas" value={petugas} onChange={(e) => setPetugas(e.target.value)} className={inputCls}>
                <option value="__ALL__">Semua Petugas Aktif</option>
                {staff.map((s) => <option key={s.id} value={s.nama}>{s.nama}</option>)}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Tanggal Mulai</label><input data-testid="bulk-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} /></div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Tanggal Akhir</label><input data-testid="bulk-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls} /></div>
          </div>

          <div className="flex rounded-xl border border-slate-200 p-1">
            <button data-testid="bulk-mode-full" onClick={() => setMode("full")} className={`flex-1 rounded-lg py-1.5 text-sm font-bold ${mode === "full" ? "bg-[#7C4DE0] text-white" : "text-slate-500"}`}>Seharian</button>
            <button data-testid="bulk-mode-custom" onClick={() => setMode("custom")} className={`flex-1 rounded-lg py-1.5 text-sm font-bold ${mode === "custom" ? "bg-[#7C4DE0] text-white" : "text-slate-500"}`}>Jam Tertentu</button>
          </div>

          {mode === "custom" && (
            <div className="flex flex-wrap gap-1.5 rounded-xl bg-slate-50 p-2" data-testid="bulk-jam-list">
              {slots.map((j) => (
                <button key={j} onClick={() => toggleJam(j)} className={`rounded-lg border px-2.5 py-1 text-xs font-bold ${jams.includes(j) ? "bg-[#7C4DE0] border-[#7C4DE0] text-white" : "bg-white border-slate-200 text-slate-500"}`}>{j}</button>
              ))}
            </div>
          )}

          {action === "block" && (
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Alasan (opsional)</label><input data-testid="bulk-alasan" value={alasan} onChange={(e) => setAlasan(e.target.value)} placeholder="mis. Cuti tahunan" className={inputCls} /></div>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
          <button data-testid="bulk-submit" onClick={submit} disabled={busy} className={`rounded-xl px-5 py-2 text-sm font-bold text-white disabled:opacity-60 ${action === "block" ? "bg-slate-700 hover:bg-slate-800" : "bg-emerald-500 hover:bg-emerald-600"}`}>{busy ? "Memproses..." : action === "block" ? "Blokir Slot" : "Buka Blokir"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SlotResCard({ r, buffer, canManage, onDetail, onReschedule }) {
  return (
    <div data-testid={`slot-res-${r.id}`} className="space-y-2 rounded-xl border border-[#E0D2FF] p-2.5">
      <div className="rounded-lg bg-[#F7F0FF] p-2.5 text-sm">
        <p className="font-bold text-slate-800">{r.jam_kunjungan} · {r.nama_lengkap}</p>
        <p className="text-slate-600">{r.jenis_layanan}</p>
        <p className="mt-1 text-xs text-slate-500">Status: <b>{r.status}</b>{r.payment_status ? ` · ${r.payment_status}` : ""}{buffer ? " · (travel buffer Homecare)" : ""}</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button data-testid={`slot-detail-btn-${r.id}`} onClick={() => onDetail(r)} className="flex items-center justify-center gap-1.5 rounded-xl bg-slate-100 py-2 text-sm font-bold text-slate-600 hover:bg-slate-200"><Info size={16} weight="duotone" /> Detail</button>
        {canManage ? (
          <button data-testid={`slot-reschedule-btn-${r.id}`} onClick={() => onReschedule(r)} className="flex items-center justify-center gap-1.5 rounded-xl bg-[#7C4DE0] py-2 text-sm font-bold text-white hover:bg-[#6A38C7]"><ArrowsClockwise size={16} weight="bold" /> Edit / Reschedule</button>
        ) : <span />}
      </div>
      {canManage && r.no_whatsapp && (
        <a data-testid={`slot-wa-btn-${r.id}`} href={waLink(r.no_whatsapp, `Halo ${r.nama_lengkap}, terkait reservasi Anda di GBS Clinic.`)} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] py-2 text-sm font-bold text-white active:scale-95"><WhatsappLogo size={18} weight="fill" /> Hubungi via WhatsApp</a>
      )}
    </div>
  );
}
